import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createFeatureRequest, uploadFeatureRequestAttachment } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Lightbulb, Paperclip, CheckCircle } from '@phosphor-icons/react';

export default function FeatureRequestPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: '', description: '', use_case: '' });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSubmitting(true);
    try {
      const res = await createFeatureRequest(form);
      if (file && res.data.id) {
        await uploadFeatureRequestAttachment(res.data.id, file);
      }
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      alert('Failed to submit feature request');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="p-6 lg:p-8 max-w-2xl mx-auto" data-testid="feature-request-success">
        <div className="text-center py-16">
          <CheckCircle size={48} weight="fill" className="text-[#34C759] mx-auto mb-4" />
          <h2 className="font-['Manrope'] text-2xl font-bold text-[#0F0F0F] mb-2">Request Submitted</h2>
          <p className="text-sm text-[#555555] mb-6">Thank you! Your feature request has been recorded and will be reviewed by the team.</p>
          <button
            onClick={() => { setSubmitted(false); setForm({ title: '', description: '', use_case: '' }); setFile(null); }}
            className="h-10 px-5 bg-[#32B4AC] text-white text-sm font-semibold rounded-md hover:bg-[#2A9B94] transition-colors"
            data-testid="submit-another-btn"
          >
            Submit Another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-2xl mx-auto" data-testid="feature-request-page">
      <div className="flex items-center gap-2 mb-1">
        <Lightbulb size={22} weight="fill" className="text-[#FFB800]" />
        <h1 className="font-['Manrope'] text-2xl font-bold tracking-tight text-[#0F0F0F]">Request a Feature</h1>
      </div>
      <p className="text-sm text-[#555555] mb-8">Have an idea to improve TechFlow? Share it with the team.</p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="border border-[#E8E8E8] rounded-md p-5 space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Feature Title *</Label>
            <Input data-testid="feature-title-input" value={form.title} onChange={update('title')} placeholder="e.g., Bulk status change for tasks" className="border-[#E8E8E8] rounded-md h-10" required />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Description</Label>
            <Textarea data-testid="feature-description-input" value={form.description} onChange={update('description')} placeholder="Describe the feature in detail..." className="min-h-[100px] border-[#E8E8E8] rounded-md resize-none" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Use Case / Problem</Label>
            <Textarea data-testid="feature-usecase-input" value={form.use_case} onChange={update('use_case')} placeholder="What problem does this solve? How would you use it?" className="min-h-[80px] border-[#E8E8E8] rounded-md resize-none" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Attachment (optional)</Label>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 px-4 py-2 border border-[#E8E8E8] rounded-md text-sm text-[#555555] hover:bg-[#F4F4F4] cursor-pointer transition-colors" data-testid="feature-attachment-btn">
                <Paperclip size={14} />
                {file ? file.name : 'Choose file'}
                <input type="file" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} />
              </label>
              {file && <button type="button" onClick={() => setFile(null)} className="text-xs text-[#555555] hover:text-[#FF3B30]">Remove</button>}
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting || !form.title.trim()}
            className="h-10 px-6 bg-[#32B4AC] text-white text-sm font-semibold rounded-md hover:bg-[#2A9B94] disabled:opacity-50 transition-colors flex items-center gap-2"
            data-testid="submit-feature-btn"
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </div>
      </form>
    </div>
  );
}
